import versions from '../assets/reaction_versions.json';

/**
 * Réactions du menu « Réagir » : emojis Fluent 3D de Microsoft (licence MIT,
 * github.com/microsoft/fluentui-emoji), images servies par Placio — même rendu
 * sur tous les systèmes. Images générées par
 * `pnpm --filter @campus/assets-cli ingest-reactions` (72 × 72 WebP,
 * apps/web/public/assets/reactions/).
 *
 * Le serveur temps réel n'accepte que ces identifiants (isReactionId).
 */
export interface Reaction {
  /** Identifiant transmis par Colyseus — stable, ne jamais renommer. */
  id: string;
  /** Caractère Unicode (texte de repli, lecteurs d'écran). */
  char: string;
  label: string;
  group: ReactionGroup;
  /** Dossier du dépôt fluentui-emoji (assets/{fluent}/…). */
  fluent: string;
  /** Emoji à couleurs de peau : image « Default » (jaune). */
  skin?: boolean;
}

export type ReactionGroup = 'approve' | 'feel' | 'party' | 'work';

export const REACTION_GROUPS: Array<{ id: ReactionGroup; label: string }> = [
  { id: 'approve', label: 'Approuver' },
  { id: 'feel', label: 'Émotions' },
  { id: 'party', label: 'Bravo' },
  { id: 'work', label: 'Travail' },
];

export const REACTIONS: Reaction[] = [
  { id: 'thumbs-up', char: '👍', label: 'Pouce levé', group: 'approve', fluent: 'Thumbs up', skin: true },
  { id: 'thumbs-down', char: '👎', label: 'Pouce baissé', group: 'approve', fluent: 'Thumbs down', skin: true },
  { id: 'clap', char: '👏', label: 'Applaudir', group: 'approve', fluent: 'Clapping hands', skin: true },
  { id: 'raised-hands', char: '🙌', label: 'Hourra', group: 'approve', fluent: 'Raising hands', skin: true },
  { id: 'wave', char: '👋', label: 'Coucou', group: 'approve', fluent: 'Waving hand', skin: true },
  { id: 'hand', char: '✋', label: 'Main levée', group: 'approve', fluent: 'Raised hand', skin: true },
  { id: 'thanks', char: '🙏', label: 'Merci', group: 'approve', fluent: 'Folded hands', skin: true },
  { id: 'ok', char: '👌', label: 'OK', group: 'approve', fluent: 'Ok hand', skin: true },
  { id: 'muscle', char: '💪', label: 'Courage', group: 'approve', fluent: 'Flexed biceps', skin: true },

  { id: 'heart', char: '❤️', label: 'Cœur', group: 'feel', fluent: 'Red heart' },
  { id: 'smile', char: '🙂', label: 'Sourire', group: 'feel', fluent: 'Slightly smiling face' },
  { id: 'wink', char: '😉', label: 'Clin d’œil', group: 'feel', fluent: 'Winking face' },
  { id: 'tongue', char: '😛', label: 'Langue tirée', group: 'feel', fluent: 'Face with tongue' },
  { id: 'joy', char: '😂', label: 'Mort de rire', group: 'feel', fluent: 'Face with tears of joy' },
  { id: 'grin', char: '😁', label: 'Grand sourire', group: 'feel', fluent: 'Beaming face with smiling eyes' },
  { id: 'love', char: '😍', label: 'J’adore', group: 'feel', fluent: 'Smiling face with heart-eyes' },
  { id: 'wow', char: '😮', label: 'Waouh', group: 'feel', fluent: 'Face with open mouth' },
  { id: 'sad', char: '😢', label: 'Triste', group: 'feel', fluent: 'Crying face' },
  { id: 'phew', char: '😅', label: 'Ouf', group: 'feel', fluent: 'Grinning face with sweat' },
  { id: 'thinking', char: '🤔', label: 'Je réfléchis', group: 'feel', fluent: 'Thinking face' },
  { id: 'eye-roll', char: '🙄', label: 'Bof', group: 'feel', fluent: 'Face with rolling eyes' },
  { id: 'mind-blown', char: '🤯', label: 'Bluffé', group: 'feel', fluent: 'Exploding head' },
  { id: 'cool', char: '😎', label: 'Trop bien', group: 'feel', fluent: 'Smiling face with sunglasses' },
  { id: 'sleepy', char: '😴', label: 'Fatigué', group: 'feel', fluent: 'Sleeping face' },
  { id: 'salute', char: '🫡', label: 'À vos ordres', group: 'feel', fluent: 'Saluting face' },

  { id: 'party', char: '🎉', label: 'Bravo', group: 'party', fluent: 'Party popper' },
  { id: 'partying', char: '🥳', label: 'Fête', group: 'party', fluent: 'Partying face' },
  { id: 'fire', char: '🔥', label: 'Génial', group: 'party', fluent: 'Fire' },
  { id: 'sparkles', char: '✨', label: 'Magique', group: 'party', fluent: 'Sparkles' },
  { id: 'rocket', char: '🚀', label: 'C’est parti', group: 'party', fluent: 'Rocket' },
  { id: '100', char: '💯', label: 'Cent pour cent', group: 'party', fluent: 'Hundred points' },
  { id: 'trophy', char: '🏆', label: 'Champion', group: 'party', fluent: 'Trophy' },
  { id: 'star', char: '⭐', label: 'Étoile', group: 'party', fluent: 'Star' },

  { id: 'check', char: '✅', label: 'Fait', group: 'work', fluent: 'Check mark button' },
  { id: 'cross', char: '❌', label: 'Non', group: 'work', fluent: 'Cross mark' },
  { id: 'question', char: '❓', label: 'Question', group: 'work', fluent: 'Red question mark' },
  { id: 'idea', char: '💡', label: 'Idée', group: 'work', fluent: 'Light bulb' },
  { id: 'eyes', char: '👀', label: 'Je regarde', group: 'work', fluent: 'Eyes' },
  { id: 'coffee', char: '☕', label: 'Pause café', group: 'work', fluent: 'Hot beverage' },
  { id: 'clock', char: '⏰', label: 'C’est l’heure', group: 'work', fluent: 'Alarm clock' },
];

const BY_ID = new Map(REACTIONS.map((r) => [r.id, r]));

export function findReaction(id: string): Reaction | undefined {
  return BY_ID.get(id);
}

export function isReactionId(id: unknown): id is string {
  return typeof id === 'string' && BY_ID.has(id);
}

/** Image de la réaction, versionnée (nginx sert les .webp en immutable). */
export function reactionUrl(id: string): string {
  const v = (versions as Record<string, string>)[id];
  return `/assets/reactions/${id}.webp${v ? `?v=${v}` : ''}`;
}

/**
 * Enchaînement : une même réaction renvoyée moins de REACTION_COMBO_MS après
 * la précédente fait monter le compteur « ×N » au lieu de repartir de zéro.
 */
export const REACTION_COMBO_MS = 1_500;

/** Limite serveur : au plus REACTION_BURST réactions par REACTION_WINDOW_MS et par personne. */
export const REACTION_BURST = 12;
export const REACTION_WINDOW_MS = 3_000;
